/**
 * Todos los números que hay que afinar, en un solo lugar.
 *
 * La regla de este archivo: cada constante dice DE DÓNDE salió y QUÉ PASA si se
 * mueve. Un número sin esa explicación es un número que nadie se va a animar a
 * tocar dentro de seis meses, y entonces deja de ser afinable.
 *
 * Si un valor todavía no se midió, se dice. Es preferible un "estimado, falta
 * medir" explícito a un literal con aire de autoridad.
 */

/** Medidas reales de una tarjeta ID-1 (ISO/IEC 7810), en milímetros. */
export const TARJETA_ID1 = {
  anchoMm: 85.6,
  altoMm: 53.98,
  /** 85,6 / 53,98 = 1,5858. Sirve para descartar rectángulos que no son la tarjeta. */
  relacionDeLados: 85.6 / 53.98,
  /**
   * Cuánto puede desviarse la relación medida y seguir siendo la tarjeta.
   *
   * MEDIDO sobre el corpus: las siete fotos de documento dan entre 1,521 y
   * 1,681, o sea desvíos de −4,1% a +6,0%. ±8% las cubre a todas, con poco
   * margen de sobra. Bajarlo empieza a rechazar fotos buenas; subirlo deja
   * pasar objetos que no son una tarjeta —una imagen vertical con las dos caras
   * apiladas da 0,793 y hay que seguir rechazándola—.
   */
  toleranciaRelacion: 0.08,
} as const;

/**
 * Alto que tiene que tener un CARÁCTER para que el OCR lo lea bien.
 *
 * Esta es la medida que importa, y no el ancho de la imagen. Medido sobre una
 * tarjeta recortada de 648 px:
 *
 *     ancho   alto de carácter   rótulos   campos leídos
 *       648         12 px           5            2
 *       972         14 px           6            3
 *      1296         19 px           7            4   ← máximo
 *      1944         28 px           5            2
 *      2592         37 px           5            2
 *
 * Ampliar no agrega información, pero el OCR tiene un tamaño mínimo por debajo
 * del cual falla aunque la información esté. Y pasado el óptimo empeora, porque
 * amplía el fondo de seguridad junto con el texto hasta que el guilloche mide lo
 * que una letra.
 *
 * Con esto se explican de una todas las mediciones que parecían contradecirse:
 * el frente de 2456 px que se leía peor ampliado ya tenía los caracteres
 * grandes; el dorso de 1280 px que se rompía a 2000 también; y esta tarjeta
 * recortada necesita el doble porque sus caracteres son chicos.
 */
export const ALTO_DE_CARACTER_OBJETIVO = 20;

/**
 * Alto típico de un carácter del frente, en milímetros.
 *
 * No es el alto de un renglón concreto: es la MEDIANA de lo que el OCR
 * devuelve, que mezcla los rótulos chicos con los valores grandes. Por eso es
 * más chico de lo que uno diría mirando el documento.
 *
 * DERIVADO de la medición: la misma tarjeta recortada leyó su máximo a 1296 px
 * de ancho, donde el carácter mediano medía 19 px. A ese ancho hay 15,1 px por
 * milímetro, así que el carácter mediano mide 19/15,1 = 1,26 mm.
 *
 * Con 2,5 mm —el alto de un renglón de nombre, que era lo que parecía
 * razonable— la escala calculada daba 685 px y el OCR no leía un solo campo.
 */
export const ALTO_DE_CARACTER_MM = 1.3;

/**
 * A qué escala llevar la imagen para que el texto quede legible para el OCR.
 *
 * No se busca a tientas: ubicada la tarjeta se conocen los píxeles por
 * milímetro, y el catálogo sabe cuánto mide el renglón en el documento real.
 * El producto da el alto actual, y de ahí sale el factor.
 */
export function escalaParaOcr(
  pxPorMmActual: number,
  altoNominalMm: number,
  objetivoPx = ALTO_DE_CARACTER_OBJETIVO
): number {
  const altoActual = pxPorMmActual * altoNominalMm;
  if (altoActual <= 0) return 1;
  return objetivoPx / altoActual;
}

/**
 * Ancho de trabajo cuando NO se pudo ubicar la tarjeta.
 *
 * Es el valor que funcionaba antes de saber calcular la escala, y queda como
 * respaldo para las fotos donde no hay con qué medir px/mm. Medido sobre el
 * frente completo: a 1600 salen los cinco campos, a 2456 se cae el apellido, a
 * 4912 no queda más que la nacionalidad.
 */
export const ANCHO_OCR = 1600;

/**
 * Cuánto se puede AGRANDAR una foto chica antes de leerla.
 *
 * Agrandar no agrega información, pero tesseract lee bastante mejor con el
 * carácter cerca de su tamaño útil, que es toda la base de
 * `ALTO_DE_CARACTER_OBJETIVO`. Medido sobre el dorso de un DNI de extranjero de
 * 545 px de ancho —un carácter de 8 px—: a su tamaño no se reconoce NINGÚN
 * rótulo, y agrandado se reconocen dos. Tres, cuatro y seis veces dan lo mismo,
 * así que el tope está para acotar el costo y no porque mejore.
 */
export const AGRANDE_MAXIMO = 4;

/**
 * Si se endereza y recorta la tarjeta antes de leer. HOY APAGADO.
 *
 * La idea es correcta y el diseño sigue en pie: las zonas del catálogo son
 * fracciones de la tarjeta, y el tapado por plantilla necesita saber dónde está.
 * Pero la IMPLEMENTACIÓN de la detección de esquinas no da la talla: es un
 * heurístico de mancha clara dominante más los extremos de x±y, y sobre fotos
 * reales hizo daño:
 *
 *   - Escorzó documentos que ya estaban derechos y bien encuadrados. El error de
 *     las esquinas se amplifica hacia el centro al aplicar la homografía.
 *   - Degradó el OCR del frente lo suficiente como para perder marcas de datos
 *     que antes se ubicaban bien.
 *   - Sobre las fotos del corpus devolvía el marco de la imagen y no la tarjeta,
 *     porque esas fotos ya vienen recortadas al documento.
 *
 * Encenderlo de nuevo requiere antes: fotos de documentos apoyados sobre algo
 * —con fondo, que es donde la detección tiene sentido— y medir sobre ellas que
 * la tarjeta se ubica de verdad, mirando las ESQUINAS y no la relación de lados.
 *
 * Mientras tanto se lee la foto como viene, que es lo que venía funcionando.
 */
export const ENDEREZADO_ACTIVO = false;

/**
 * Ancho al que se entrega la tarjeta ya enderezada.
 *
 * 1600 px sobre 85,6 mm son ~475 dpi: de sobra para que una persona la lea y
 * para que alguien la relea con otro OCR. Subirlo solo engorda el JPEG.
 */
export const ANCHO_ENTREGA = 1600;

/**
 * Demasía del recorte, como fracción del ancho de la tarjeta.
 *
 * La tarjeta se ubica por anclas que son estimaciones —la fracción en que vive
 * el código de barras, la altura a la que arranca el MRZ— y cada una arrastra
 * su error. Ante la duda conviene que sobre y no que falte: unos milímetros de
 * mesa alrededor no le molestan a nadie, y un renglón cortado se pierde.
 *
 * Ya pasó una vez: con la demasía en 2% y el arranque del MRZ mal estimado, el
 * recorte del dorso se comía la línea del domicilio.
 */
export const DEMASIA_DEL_RECORTE = 0.06;

/** Píxeles por milímetro que implica un ancho de tarjeta dado. */
export function pxPorMm(anchoTarjetaPx: number): number {
  return anchoTarjetaPx / TARJETA_ID1.anchoMm;
}

/**
 * Umbrales de legibilidad HUMANA. No miden si el OCR pudo: miden si una persona
 * puede. Son cosas distintas y ya las vimos separarse en las dos direcciones:
 *
 *   - El espécimen 2020 es legible a simple vista y el OCR no devolvió un dato.
 *   - Una captura de webcam fuera de foco entregó el MRZ entero y de ahí una
 *     persona no puede leer ni el nombre ni el domicilio.
 *
 * Por eso ninguno de estos números mira la salida del OCR.
 */
export const LEGIBILIDAD = {
  /**
   * Desenfoque tolerado, de 0 (nítida) a 1 (papilla).
   *
   * MEDIDO, y todas estas se leen a simple vista: 2020-Frente 0,18 ·
   * 2020-Reverso 0,21 · wikipedia 0,21 · 2026-frente 0,49 · 2026-dorso 0,571.
   *
   * El corte estuvo en 0,60 y fue demasiado justo: el dorso 2026 mide 0,571 y
   * es perfectamente legible, así que un remuestreo lo empujaba encima del
   * umbral y la pantalla decía "está fuera de foco" de una foto impecable.
   *
   * Y una limitación que hay que decir: NO tenemos una sola imagen ilegible en
   * el corpus, así que el extremo de arriba está sin calibrar. Estos cortes se
   * eligen para que nada de lo que sabemos legible los toque, y no al revés.
   * Como esto avisa y no bloquea, errar hacia el silencio es lo barato.
   */
  desenfoqueDudoso: 0.7,
  desenfoqueIlegible: 0.85,

  /**
   * Fracción de la zona tapada por reflejo, medida como teselas claras y sin
   * detalle.
   *
   * PENDIENTE DE CALIBRAR con fotos reales: el corpus que tenemos son renders,
   * y en un render el fondo en blanco es exactamente 255, indistinguible de una
   * mancha de brillo. En una foto el papel da 230-245 y el reflejo 255, así que
   * la medida separa bien; en un render no. Los dorsos del corpus dan 17-18% y
   * no tienen ni un reflejo.
   */
  reflejoDudoso: 0.2,
  reflejoIlegible: 0.35,

  /** Zona hundida en sombra. */
  oscuridadMaxima: 0.2,

  /**
   * Alto mínimo de un renglón impreso, en píxeles, para leer en pantalla.
   * Sale de la geometría —px/mm × alto nominal del catálogo— y no del OCR.
   *
   * MEDIDO a ojo sobre el corpus, a tamaño nativo y con la tarjeta ubicada:
   * renglones de 11,6 y 12,6 px se leen bien; uno de 8,6 px, con esfuerzo, y uno
   * de 6,6 px apenas. Los 20 px de antes («el piso clásico», estimado) decían
   * «muy chico» de fotos que se leen perfecto: la mitad de las fotos reales, que
   * llegan por WhatsApp a 720 px, y cada cara de una foto con las dos.
   */
  altoRenglonMinimoPx: 11,
} as const;

/**
 * Umbral de inclinación a partir del cual se endereza.
 *
 * Por qué pocos grados importan: una línea del MRZ TD1 son 30 caracteres, unos
 * 76 mm. A 3° el renglón se desplaza ~4 mm en vertical de una punta a la otra,
 * más de lo que mide un renglón de alto: el OCR deja de poder separar los tres.
 *
 * Debajo de este ángulo no vale la pena pagar el OCR extra del segundo intento.
 */
export const INCLINACION = {
  gradosTolerados: 1.5,
  /** Pasado esto, si no se pudo enderezar, se declara y se ofrece borrado a mano. */
  gradosSinGarantia: 6,
} as const;

/**
 * Arqueo de las aristas de la tarjeta por distorsión de lente (barril).
 *
 * PENDIENTE DE MEDIR. La homografía manda rectas a rectas, así que si los bordes
 * salen curvos el ajuste calza en las esquinas y queda mal en el medio. El error
 * residual corre los recortes de zona y, sobre todo, el rectángulo del Nº de
 * Trámite, que es la garantía.
 *
 * Se expresa como fracción del ancho de la tarjeta: la flecha máxima de una
 * arista respecto de la recta entre sus dos esquinas.
 */
export const DISTORSION = {
  /** Debajo de esto se ignora. Valor de arranque, SIN MEDIR. */
  arqueoDespreciable: 0.005,
  /** Pasado esto, la posición de las zonas no es confiable: se declara. */
  arqueoSinGarantia: 0.02,
} as const;

/**
 * Confianza que se le asigna a un campo según CÓMO quedó verificado.
 *
 * El tope de `lectura_unica` es lo importante de esta tabla: una lectura sola no
 * puede llegar arriba por más que el OCR diga 0,96. El verificador compuesto del
 * MRZ TD1 saltea la posición del sexo y la de la nacionalidad, así que ahí la
 * confianza del OCR es lo único que hay, y no alcanza.
 */
export const CONFIANZA = {
  digitoDeControl: 1.0,
  coincidenciaMultiple: 0.9,
  /** Techo de una lectura sin respaldo, por más segura que se muestre el OCR. */
  topeLecturaUnica: 0.7,
  enConflicto: 0.3,
} as const;
